import React, { useState, useMemo } from 'react'
import { useThemeTokens } from '../themes'
import { Card, Button, Badge, SearchInput } from '../components/ui'
import type { ESSCOProduct, ESSCOFilterOptions } from '../types/essco'
import esscoData from '../data/essco_products.json'

// Type for the consolidated product structure
interface ConsolidatedESSCOProduct extends ESSCOProduct {
  isLegacyProduct?: boolean
  legacySource?: string
  historicalData?: {
    unit: string
    qtyPerUnit: number
    pricePerUnit: number
    perItem: number
    orders: number
    source: string
    category: string
    note: string
  }
  availabilityStatus?: string
  recommendation?: string
}

const ESSCOProducts: React.FC = () => {
  const theme = useThemeTokens()
  const [filters, setFilters] = useState<ESSCOFilterOptions>({})
  const [currentPage, setCurrentPage] = useState(1)
  const productsPerPage = 20

  // Type the imported data as consolidated products
  const products = esscoData.products as ConsolidatedESSCOProduct[]

  // Get unique categories for filter dropdown
  const categories = useMemo(() => {
    const allCategories = products.flatMap(p => p.categories.map(c => c.name))
    return [...new Set(allCategories)].sort()
  }, [products])

  // Filter and search products
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      // Search term filter
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase()
        const matchesSearch = 
          product.name.toLowerCase().includes(searchLower) ||
          product.description.toLowerCase().includes(searchLower) ||
          product.categories.some(cat => cat.name.toLowerCase().includes(searchLower))
        
        if (!matchesSearch) return false
      }

      // Category filter
      if (filters.category) {
        const hasCategory = product.categories.some(cat => cat.name === filters.category)
        if (!hasCategory) return false
      }

      // Stock filter
      if (filters.inStock !== undefined) {
        if (product.isInStock !== filters.inStock) return false
      }

      // Price filters
      if (filters.priceMin !== undefined && product.price && product.price < filters.priceMin) {
        return false
      }
      
      if (filters.priceMax !== undefined && product.price && product.price > filters.priceMax) {
        return false
      }

      return true
    })
  }, [products, filters])

  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage)
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * productsPerPage,
    currentPage * productsPerPage
  )

  // Stats for display
  const inStockCount = products.filter(p => p.isInStock && !p.isLegacyProduct).length
  // const outOfStockCount = products.filter(p => !p.isInStock && !p.isLegacyProduct).length
  const legacyProductCount = products.filter(p => p.isLegacyProduct).length
  const enhancedProductCount = products.filter(p => p.historicalData && !p.isLegacyProduct).length

  const pageStyles: React.CSSProperties = {
    padding: theme.spacing.lg,
    maxWidth: '1200px',
    margin: '0 auto'
  }

  const headerStyles: React.CSSProperties = {
    marginBottom: theme.spacing.xl,
    textAlign: 'center'
  }

  const statsStyles: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: theme.spacing.md,
    marginBottom: theme.spacing.xl
  }

  const filtersStyles: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: theme.spacing.md,
    marginBottom: theme.spacing.lg,
    padding: theme.spacing.md,
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.md,
    border: `1px solid ${theme.colors.border}`
  }

  const productsGridStyles: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: theme.spacing.lg,
    marginBottom: theme.spacing.xl
  }

  const productImageStyles: React.CSSProperties = {
    width: '100%',
    height: '200px',
    objectFit: 'cover',
    borderRadius: theme.borderRadius.sm,
    backgroundColor: theme.colors.surface
  }

  const paginationStyles: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing.sm,
    marginTop: theme.spacing.xl
  }

  return (
    <div style={pageStyles}>
      {/* Header */}
      <div style={headerStyles}>
        <h1 style={{ 
          fontSize: theme.typography.fontSize.xl, 
          fontWeight: theme.typography.fontWeight.bold,
          color: theme.colors.text,
          marginBottom: theme.spacing.sm
        }}>
          ESSCO Products Inventory
        </h1>
        <p style={{ 
          color: theme.colors.textSecondary,
          fontSize: theme.typography.fontSize.lg
        }}>
          Browse {esscoData.metadata.totalProducts} products from consolidated ESSCO catalog
          <br />
          <span style={{ fontSize: theme.typography.fontSize.sm }}>
            {esscoData.metadata.originalApiProducts} API products • {esscoData.metadata.legacyProducts} legacy products
          </span>
        </p>
      </div>

      {/* Stats Cards */}
      <div style={statsStyles}>
        <Card>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              fontSize: theme.typography.fontSize.xl, 
              fontWeight: theme.typography.fontWeight.bold,
              color: theme.colors.primary 
            }}>
              {products.length}
            </div>
            <div style={{ color: theme.colors.textSecondary }}>Total Products</div>
          </div>
        </Card>
        
        <Card>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              fontSize: theme.typography.fontSize.xl, 
              fontWeight: theme.typography.fontWeight.bold,
              color: theme.colors.success 
            }}>
              {inStockCount}
            </div>
            <div style={{ color: theme.colors.textSecondary }}>In Stock</div>
          </div>
        </Card>
        
        <Card>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              fontSize: theme.typography.fontSize.xl, 
              fontWeight: theme.typography.fontWeight.bold,
              color: theme.colors.primary 
            }}>
              {enhancedProductCount}
            </div>
            <div style={{ color: theme.colors.textSecondary }}>With Order History</div>
          </div>
        </Card>
        
        <Card>
          <div style={{ textAlign: 'center' }}>
            <div style={{ 
              fontSize: theme.typography.fontSize.xl, 
              fontWeight: theme.typography.fontWeight.bold,
              color: theme.colors.warning 
            }}>
              {legacyProductCount}
            </div>
            <div style={{ color: theme.colors.textSecondary }}>Legacy Products</div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <div style={filtersStyles}>
        <div>
          <label style={{ 
            display: 'block', 
            marginBottom: theme.spacing.sm,
            fontSize: theme.typography.fontSize.sm,
            fontWeight: theme.typography.fontWeight.medium
          }}>
            Search Products
          </label>
          <SearchInput
            value={filters.searchTerm || ''}
            onChange={(e) => setFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
            placeholder="Search by name, description, or category..."
          />
        </div>

        <div>
          <label style={{ 
            display: 'block', 
            marginBottom: theme.spacing.sm,
            fontSize: theme.typography.fontSize.sm,
            fontWeight: theme.typography.fontWeight.medium
          }}>
            Category
          </label>
          <select
            value={filters.category || ''}
            onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value || undefined }))}
            style={{
              width: '100%',
              padding: theme.spacing.sm,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.sm,
              fontSize: theme.typography.fontSize.sm
            }}
          >
            <option value="">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ 
            display: 'block', 
            marginBottom: theme.spacing.sm,
            fontSize: theme.typography.fontSize.sm,
            fontWeight: theme.typography.fontWeight.medium
          }}>
            Stock Status
          </label>
          <select
            value={filters.inStock === undefined ? '' : filters.inStock.toString()}
            onChange={(e) => {
              const value = e.target.value
              setFilters(prev => ({ 
                ...prev, 
                inStock: value === '' ? undefined : value === 'true'
              }))
            }}
            style={{
              width: '100%',
              padding: theme.spacing.sm,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: theme.borderRadius.sm,
              fontSize: theme.typography.fontSize.sm
            }}
          >
            <option value="">All Items</option>
            <option value="true">In Stock Only</option>
            <option value="false">Out of Stock Only</option>
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'end' }}>
          <Button
            onClick={() => {
              setFilters({})
              setCurrentPage(1)
            }}
            variant="secondary"
          >
            Clear Filters
          </Button>
        </div>
      </div>

      {/* Results Count */}
      <div style={{ 
        marginBottom: theme.spacing.lg,
        fontSize: theme.typography.fontSize.sm,
        color: theme.colors.textSecondary
      }}>
        Showing {paginatedProducts.length} of {filteredProducts.length} products
      </div>

      {/* Products Grid */}
      <div style={productsGridStyles}>
        {paginatedProducts.map(product => (
          <Card key={product.id}>
            {/* Product Image */}
            {product.images.length > 0 && (
              <img
                src={product.images[0].url}
                alt={product.images[0].altText || product.name}
                style={productImageStyles}
                onError={(e) => {
                  // Hide broken images
                  (e.target as HTMLImageElement).style.display = 'none'
                }}
              />
            )}

            {/* Product Info */}
            <div style={{ padding: theme.spacing.md, flexGrow: 1 }}>
              <div style={{ marginBottom: theme.spacing.sm }}>
                {product.isLegacyProduct ? (
                  <Badge variant="draft">Legacy Product</Badge>
                ) : (
                  <Badge variant={product.isInStock ? 'confirmed' : 'draft'}>
                    {product.isInStock ? 'In Stock' : 'Out of Stock'}
                  </Badge>
                )}
                {product.historicalData && !product.isLegacyProduct && (
                  <span style={{ marginLeft: theme.spacing.sm }}>
                    <Badge variant="submitted">
                      Has Order History
                    </Badge>
                  </span>
                )}
                {product.quantityAvailable > 0 && !product.isLegacyProduct && (
                  <span style={{ 
                    marginLeft: theme.spacing.sm,
                    fontSize: theme.typography.fontSize.sm,
                    color: theme.colors.textSecondary
                  }}>
                    ({product.quantityAvailable} available)
                  </span>
                )}
              </div>

              <h3 style={{ 
                fontSize: theme.typography.fontSize.lg,
                fontWeight: theme.typography.fontWeight.bold,
                marginBottom: theme.spacing.sm,
                color: theme.colors.text
              }}>
                {product.name}
              </h3>

              <p style={{ 
                fontSize: theme.typography.fontSize.sm,
                color: theme.colors.textSecondary,
                marginBottom: theme.spacing.sm,
                lineHeight: 1.4
              }}>
                {product.description}
              </p>

              {/* Pricing Section */}
              <div style={{ marginBottom: theme.spacing.sm }}>
                {product.isLegacyProduct && product.historicalData ? (
                  <>
                    <div style={{ 
                      fontSize: theme.typography.fontSize.lg,
                      fontWeight: theme.typography.fontWeight.bold,
                      color: theme.colors.primary
                    }}>
                      ${product.historicalData.pricePerUnit.toFixed(2)} per {product.historicalData.unit}
                    </div>
                    <div style={{ 
                      fontSize: theme.typography.fontSize.sm,
                      color: theme.colors.textSecondary
                    }}>
                      ${product.historicalData.perItem.toFixed(2)} per item • Ordered {product.historicalData.orders} times
                    </div>
                  </>
                ) : product.historicalData ? (
                  <>
                    <div style={{ 
                      fontSize: theme.typography.fontSize.lg,
                      fontWeight: theme.typography.fontWeight.bold,
                      color: theme.colors.primary
                    }}>
                      {product.priceFormatted || `$${product.price?.toFixed(2)}`}
                    </div>
                    <div style={{ 
                      fontSize: theme.typography.fontSize.sm,
                      color: theme.colors.textSecondary
                    }}>
                      Historical: ${product.historicalData.pricePerUnit.toFixed(2)} per {product.historicalData.unit} • Ordered {product.historicalData.orders} times
                    </div>
                  </>
                ) : product.price ? (
                  <div style={{ 
                    fontSize: theme.typography.fontSize.lg,
                    fontWeight: theme.typography.fontWeight.bold,
                    color: theme.colors.primary
                  }}>
                    {product.priceFormatted}
                  </div>
                ) : null}
              </div>

              {product.categories.length > 0 && (
                <div style={{ marginBottom: theme.spacing.sm }}>
                  {product.categories.slice(0, 2).map(category => (
                    <span key={category.id} style={{ marginRight: theme.spacing.sm, display: 'inline-block' }}>
                      <Badge variant="draft">
                        {category.name}
                      </Badge>
                    </span>
                  ))}
                </div>
              )}

              <div style={{ 
                fontSize: theme.typography.fontSize.sm,
                color: theme.colors.textSecondary 
              }}>
                ID: {product.id}
                {product.historicalData && (
                  <> • Source: {product.historicalData.source}</>
                )}
                {product.isLegacyProduct && product.recommendation && (
                  <div style={{ 
                    fontSize: theme.typography.fontSize.sm,
                    color: theme.colors.warning,
                    marginTop: '4px',
                    fontStyle: 'italic'
                  }}>
                    {product.recommendation}
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={paginationStyles}>
          <Button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            variant="secondary"
          >
            First
          </Button>
          
          <Button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            variant="secondary"
          >
            Previous
          </Button>

          <span style={{ 
            padding: `0 ${theme.spacing.md}`,
            fontSize: theme.typography.fontSize.sm
          }}>
            Page {currentPage} of {totalPages}
          </span>

          <Button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            variant="secondary"
          >
            Next
          </Button>
          
          <Button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            variant="secondary"
          >
            Last
          </Button>
        </div>
      )}
    </div>
  )
}

export { ESSCOProducts } 